import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowUp, Columns3, Save } from "lucide-react";
import { Button } from "../../components/Button";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";

// 報價單列表要顯示哪幾欄、照什麼順序。
//
// ── 為什麼在列表頁上,不在設定頁 ────────────────────────────────────
//
// 業主原話 2026-09-13:「再設定裡面的列表欄位顯示 搬到報價單列表頁面
// 不要單獨去設定頁面 會不清楚」。改的是這一頁的樣子,調整的入口就該在這一頁上,
// 而且**只有這一個入口** —— 設定頁那個分頁已經移除。同一件事留兩個入口比
// 放錯地方更糟:兩邊各存各的,使用者不知道最後是誰說了算。
//
// ── 每個人一份 ──────────────────────────────────────────────────
//
// 業主 2026-09-25:「報價單中的顯示欄位 每個人都可以用 設定都顯示在自己帳號下
// 不是COOKIE」。所以存在**自己的帳號上**(`/api/users/me/list-columns`),
// 不是 localStorage —— 換一台電腦也跟著人走。每個角色都按得動,改的永遠是自己。
// (09-13 到 09-25 之間是全公司一套、只有老闆改得動。)
//
// ── 這一支刻意不知道任何一個中文標題 ──────────────────────────────────
//
// 可選欄位的鍵與中文標題只在後端 `app/columns.py` 的 AVAILABLE 維護一份,
// 隨 `GET /api/settings` 的 `available_columns` 一起下來。前端若自己再抄一份,
// 兩份遲早會對不起來:改了其中一邊,浮層與表頭就會各叫各的名字。
//
// 唯一留在前端的是**欄位鍵**(識別字,不是給人看的字),而且只用在
// 「設定整個讀不到」的退路上 —— 見 FALLBACK_COLUMNS。

/** 浮層寬度。一列是「勾選框 + 標題 + 位置 + 兩顆箭頭」,窄了會擠成兩行。 */
const PANEL_WIDTH = 264;
/** 選項區的高度上限。可選欄位有十幾個,超過的部分在浮層裡面捲。 */
const LIST_MAX_HEIGHT = "18rem";
/** 浮層的概估高度,用來判斷要往下開還是往上翻。 */
const PANEL_HEIGHT = 420;

/** `GET /api/users/me/list-columns` 的回應:我的欄位與可選欄位的中文標題。 */
export interface ListColumnSettings {
  /** 要顯示的欄位鍵,**陣列本身就是順序**。 */
  quote_list_columns?: string[];
  /** 欄位鍵 → 中文標題。唯一一份,由後端提供。 */
  available_columns?: Record<string, string>;
}

/**
 * 設定整個讀不到時的退路,對應後端 `app/columns.py` 的 DEFAULT。
 *
 * 這裡只有識別字,沒有任何一個中文標題 —— 標題還是只能等後端。
 * 真的連設定都要不到時,列表寧可用欄位鍵當表頭(看得出是系統出問題),
 * 也不要前端自己掰一組標題出來假裝一切正常。
 */
export const FALLBACK_COLUMNS = [
  "quote_no",
  "title",
  "client",
  "quote_date",
  // owner 不是可有可無的一欄(標題仍然只由後端命名,這裡不寫中文):四個表頭
  // 漏斗各長在自己那一欄上,欄位沒顯示,那一欄的漏斗就整個不存在,
  // 也沒有別的入口可以按那個條件篩。
  "owner",
  "status",
  "total",
];

/**
 * 整理成可以直接拿去畫表頭的清單:不認得的鍵丟掉、重複的只留第一次、
 * 一個都不剩就退回預設。與後端 `columns.normalize` 同一套規則。
 *
 * 為什麼前端也要做一次:後端存的是舊設定時它自己就會過濾,但
 * 「設定要不到、走 FALLBACK」那條路沒有經過後端,仍然要有人把關。
 */
export function normalizeColumns(keys: string[] | undefined, available: Record<string, string>): string[] {
  const known = new Set(Object.keys(available));
  const out: string[] = [];
  for (const key of keys ?? []) {
    // 後端還沒回來時 available 是空的,這時不拿它當「都不認得」來砍。
    if (known.size > 0 && !known.has(key)) continue;
    if (out.includes(key)) continue;
    out.push(key);
  }
  if (out.length > 0) return out;
  // 退路自己也要有退路:後端把欄位鍵整批改名時,`known` 與 FALLBACK 可能一個都對不上,
  // 這時若回空陣列,列表會畫出一張沒有任何欄位、連一顆按鈕都沒有的表 ——
  // 整頁報價單都打不開。寧可用欄位鍵當表頭(看得出是系統出問題),也不要一片空白。
  const fromFallback = known.size > 0 ? FALLBACK_COLUMNS.filter((key) => known.has(key)) : [];
  return fromFallback.length > 0 ? fromFallback : [...FALLBACK_COLUMNS];
}

export interface ColumnPickerProps {
  /** 目前顯示中的欄位鍵,順序就是左到右。 */
  columns: string[];
  /** 欄位鍵 → 中文標題,來自後端。 */
  available: Record<string, string>;
  /** 存檔成功後把新的欄位順序交回列表,表頭當場就跟著變,不必重新整理。 */
  onSaved: (next: string[]) => void;
}

export function ColumnPicker({ columns, available, onSaved }: ColumnPickerProps) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const [chosen, setChosen] = useState<string[]>(columns);
  const [saving, setSaving] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelId = `nq-column-picker-${useId()}`;

  /** 依按鈕的位置算出浮層的固定座標,下方空間不足時往上翻。 */
  const place = useCallback(() => {
    const anchor = triggerRef.current;
    if (
      !anchor ||
      typeof window === "undefined" ||
      typeof anchor.getBoundingClientRect !== "function"
    ) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < PANEL_HEIGHT && rect.top > spaceBelow;
    // 按鈕在右上角,浮層靠右對齊它 —— 靠左會讓浮層往畫面外長。
    const left = Math.max(8, Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8));
    setStyle({
      position: "fixed",
      left,
      width: PANEL_WIDTH,
      maxWidth: "calc(100vw - 1rem)",
      ...(flipUp
        ? { bottom: Math.max(8, window.innerHeight - rect.top + 4) }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  /** 關閉並把焦點還給按鈕 —— 鍵盤使用者不能被丟回頁首。 */
  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const toggleOpen = () => {
    if (open) {
      close();
      return;
    }
    // 每次打開都從伺服器上那一份重新開始:上次點開改了一半又關掉的選擇
    // 不該偷偷留著,否則下一次按儲存會存到自己沒打算存的東西。
    setChosen(columns);
    place();
    setOpen(true);
  };

  // 開著的時候跟著捲動與縮放走。用捕獲階段才收得到容器自己的捲動。
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  // 點到浮層與按鈕以外的地方就收起來。這裡不還焦點:使用者是用滑鼠離開的。
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (document.getElementById(panelId)?.contains(target)) return;
      close(false);
    };
    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [open, panelId, close]);

  const onPanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  const toggle = (key: string) => {
    setChosen((prev) =>
      prev.includes(key)
        ? prev.filter((one) => one !== key)
        : // 新勾的排在最後面,不要偷偷插隊到中間去。
          [...prev, key],
    );
  };

  /** 往上或往下換一格。頭尾兩端的按鈕是停用的,所以這裡只處理換得動的情況。 */
  const move = (key: string, delta: number) => {
    setChosen((prev) => {
      const at = prev.indexOf(key);
      const to = at + delta;
      if (at < 0 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      next[at] = prev[to];
      next[to] = prev[at];
      return next;
    });
  };

  // 一欄都不勾不是一種設定,是把整張表弄成空白。按鈕停用之外還要說出理由 ——
  // 只變灰的話,老闆只知道「按不下去」,不知道自己該做什麼才按得下去。
  const nothingPicked = chosen.length === 0;
  // 後端的欄位清單還沒到、或根本要不到。
  const noOptions = Object.keys(available).length === 0;

  const save = () => {
    if (nothingPicked || noOptions || saving) return;
    setSaving(true);
    api
      .put<ListColumnSettings>("/users/me/list-columns", { quote_list_columns: chosen })
      .then((updated) => {
        const labels = updated?.available_columns ?? available;
        onSaved(normalizeColumns(updated?.quote_list_columns ?? chosen, labels));
        toast.push("已儲存", "success");
        close();
      })
      .catch((err) => toast.push(err instanceof Error ? err.message : "儲存失敗", "error"))
      .finally(() => setSaving(false));
  };

  // 勾起來的照使用者排的順序在上面,沒勾的照後端給的順序排在下面 ——
  // 老闆看到的次序,就是他收起浮層之後在表頭上看到的次序。
  const picked = chosen.filter((key) => key in available);
  const rows = [...picked, ...Object.keys(available).filter((key) => !picked.includes(key))];

  const panel =
    open && style !== null && typeof document !== "undefined"
      ? createPortal(
          <div
            id={panelId}
            role="dialog"
            aria-label="顯示欄位"
            style={style}
            onKeyDown={onPanelKeyDown}
            className={
              "z-50 flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] " +
              "bg-[var(--surface)] p-2 shadow-card"
            }
          >
            <p className="m-0 px-0.5 text-xs text-[var(--text-3)]">
              勾要顯示的欄,再用上下鍵排出左到右的次序。全公司共用一套。
            </p>

            <ul
              style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: "auto" }}
              className="m-0 flex list-none flex-col gap-1 p-0"
            >
              {rows.map((key) => {
                const at = chosen.indexOf(key);
                const on = at >= 0;
                const label = available[key];
                return (
                  <li key={key} className="flex items-center gap-1.5">
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm text-[var(--text)]">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(key)}
                        className="h-4 w-4 shrink-0 accent-[var(--accent)]"
                      />
                      <span className="truncate">{label}</span>
                    </label>

                    {/* 顯示與否用文字講,不靠灰階或位置暗示。 */}
                    <span className="tabular shrink-0 text-xs text-[var(--text-3)]">
                      {on ? `第 ${at + 1} 欄` : "未顯示"}
                    </span>

                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`上移「${label}」`}
                      title={on ? undefined : "先勾起來才排得了次序"}
                      disabled={!on || at === 0}
                      onClick={() => move(key, -1)}
                      icon={<ArrowUp aria-hidden="true" size={14} />}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`下移「${label}」`}
                      title={on ? undefined : "先勾起來才排得了次序"}
                      disabled={!on || at === chosen.length - 1}
                      onClick={() => move(key, 1)}
                      icon={<ArrowDown aria-hidden="true" size={14} />}
                    />
                  </li>
                );
              })}
            </ul>

            {/* 欄位清單要不到就什麼都別讓他存:這時 available 是空的,畫面上
                一個勾選都沒有,按下去卻會把退路那六欄當成他的選擇存進去。 */}
            {noOptions ? (
              <p role="alert" className="m-0 px-0.5 text-sm text-[var(--danger)]">
                欄位清單載入失敗,請稍後再試。
              </p>
            ) : null}

            {nothingPicked && !noOptions ? (
              <p role="alert" className="m-0 px-0.5 text-sm text-[var(--danger)]">
                至少要保留一欄,否則報價單列表會是一片空白。
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] pt-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={nothingPicked || noOptions}
                loading={saving}
                onClick={save}
                icon={<Save aria-hidden="true" size={14} />}
              >
                儲存欄位
              </Button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="secondary"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={toggleOpen}
        icon={<Columns3 aria-hidden="true" size={16} />}
      >
        顯示欄位
      </Button>
      {panel}
    </>
  );
}
